import {
  createComponentMapperModel,
  withCompInfo,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import {
  withFallbackValues,
  getFocusRingValue,
  convertPhysicalAlignmentToLogical,
  castToInheritIfNeeded,
  castFromInheritIfNeeded,
} from '@wix/editor-elements-common-utils';
import type { ITabsDefinition } from '../documentManagement/Tabs.definition';
import type {
  ITabsCarmiData,
  ITabsMapperProps,
  ITabsProperties,
} from '../Tabs.types';
import { isVerticalProperties } from '../Tabs.types';
import { toBoolean } from '../utils';
import {
  getFlexDirectionVars,
  getMenuModeCSSVars,
  getTabsListAlignmentCssVars,
  getTabsListDirectionCSSVars,
  sumArgs,
  toPxValue,
} from './TabsMapperUtils';

export const props = withCompInfo<
  ITabsMapperProps,
  ITabsDefinition,
  ITabsCarmiData
>()(
  [
    'compData',
    'compProps',
    'compStylableClass',
    'hasResponsiveLayout',
    'isMasterPage',
  ],
  (
    {
      compData,
      compProps,
      compStylableClass,
      hasResponsiveLayout,
      isMasterPage,
    },
    carmiData,
  ) => {
    const { tabItems, containerProps, containerRootClassName } = carmiData;
    const { defaultTabId } = compData;

    return {
      ...compData,
      ...compProps,
      stylableClassName: compStylableClass,
      tabItems,
      currentTabId: defaultTabId,
      hasResponsiveLayout,
      containerProps,
      containerRootClassName,
      isMasterPage,
    };
  },
);

export const css = withCompInfo<
  any,
  ITabsDefinition,
  never,
  {
    styleProperties: ITabsProperties;
  }
>()(
  [
    'compProps',
    'hasResponsiveLayout',
    'styleProperties',
    'experiments',
    'compData',
    'directionWithOptInRtlSupport',
  ],
  ({
    styleProperties,
    hasResponsiveLayout,
    compData,
    directionWithOptInRtlSupport,
  }) => {
    const {
      menuMode,
      itemsOrientation = 'horizontal',
      containerSpacing,
      itemSpacing,
      horizontalTabPadding,
      verticalTabPadding,
      stretchTabsToMenuWidth,
      itemRowSpacing,
      tabEnterAnimationName = 'none',
      tabExitAnimationName = 'none',
      animationDuration = 0.3,
      itemsLogicalAlign,
      contentLogicalAlign,
    } = styleProperties;
    const { direction } = compData;
    const isHorizontal = itemsOrientation === 'horizontal';
    const itemsDirection = castFromInheritIfNeeded(
      direction,
      directionWithOptInRtlSupport,
    );
    const isRtl = itemsDirection === 'rtl';

    const responsiveContainerVars = {
      '--rd': '0',
      '--brw': '0',
      '--shd': 'none',
      '--bg': 'transparent',
    };

    const gapVars = isHorizontal
      ? {
          '--tabs-list-items-col-gap': toPxValue(itemSpacing),
          '--tabs-list-items-row-gap': toPxValue(itemRowSpacing),
        }
      : {
          '--tabs-list-items-row-gap': toPxValue(itemSpacing),
        };

    const tabsListCommonLayoutCSSVars = {
      ...gapVars,
      '--tabs-list-container-gap': toPxValue(containerSpacing),
      '--tabs-list-content-alignment': contentLogicalAlign,
      '--tabs-list-horizontal-padding': toPxValue(horizontalTabPadding),
      '--tabs-list-vertical-padding': toPxValue(verticalTabPadding),
      '--tabs-list-item-flex-grow': toBoolean(stretchTabsToMenuWidth)
        ? '1'
        : '0',
      '--tabs-list-orientation': itemsOrientation,
      '--tabs-list-direction': itemsDirection,
    };

    const tabsListLayoutCSSVars = {
      ...tabsListCommonLayoutCSSVars,
      ...(isVerticalProperties(styleProperties)
        ? {
            '--tabs-list-width': toPxValue(
              sumArgs(styleProperties.tabWidth, horizontalTabPadding),
            ),
            '--tabs-list-height': '100%',
            '--tabs-list-position': 'absolute',
            '--tabs-position': 'relative',
            [`--tabs-padding-${isRtl ? 'right' : 'left'}`]: toPxValue(
              sumArgs(
                styleProperties.tabWidth,
                horizontalTabPadding,
                containerSpacing,
              ),
            ),
            '--tab-label-overflow': 'hidden',
            '--tab-label-text-overflow': 'ellipsis',
          }
        : {
            '--tabs-list-width': '100%',
            '--tabs-list-height': 'auto',
            '--tabs-list-position': 'relative',
          }),
    };

    const menuModeCSSVars = getMenuModeCSSVars(
      menuMode,
      isHorizontal,
      itemsLogicalAlign,
    );

    const tabsDirectionVars = getFlexDirectionVars(isHorizontal, isRtl);

    const tabsListAlignmentCssVars = getTabsListAlignmentCssVars(
      menuMode,
      isHorizontal,
      itemsLogicalAlign,
    );

    const tabsListDirectionVars = getTabsListDirectionCSSVars(
      itemsDirection,
      isHorizontal,
    );

    return {
      height: 'auto',
      ...(hasResponsiveLayout
        ? {
            ...responsiveContainerVars,
            // For responsive vertical layout, adjust SingleTab width to fit Tabs container when switching from horizontal.
            // Set wrapper width to 0px, so width is determined by flex: 1.
            '--multi-state-wrapper-width': isHorizontal ? '100%' : '0px',
          }
        : {
            ...(!isHorizontal && {
              'min-width': 'fit-content',
              'max-width': 'fit-content',
            }),
            '--multi-state-wrapper-width': '100%',
          }),
      ...menuModeCSSVars,
      ...tabsDirectionVars,
      ...tabsListLayoutCSSVars,
      ...tabsListAlignmentCssVars,
      ...tabsListDirectionVars,
      '--focus-ring': getFocusRingValue(menuMode === 'wrap'),
      '--tab-animation-duration': `${animationDuration / 2}s`,
      '--tab-enter-animation-name': tabEnterAnimationName,
      '--tab-exit-animation-name': tabExitAnimationName,
      '--bg-gradient': 'none',
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [
          'menuMode',
          'itemsDirection',
          'itemsOrientation',
          'itemsAlignment',
          'containerSpacing',
          'itemSpacing',
          'horizontalTabPadding',
          'verticalTabPadding',
          'contentAlignment',
          'stretchTabsToMenuWidth',
          'itemRowSpacing',
          'tabWidth',
          'animationDuration',
          'tabEnterAnimationName',
          'tabExitAnimationName',
        ],
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'itemsAlignment', target: 'itemsLogicalAlign' }],
        enhancer: (value, { styleProperties }) => {
          return convertPhysicalAlignmentToLogical(
            value,
            styleProperties.itemsDirection,
          );
        },
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'contentAlignment', target: 'contentLogicalAlign' }],
        enhancer: (value, { styleProperties }) => {
          return convertPhysicalAlignmentToLogical(
            value,
            styleProperties.itemsDirection,
          );
        },
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: [{ source: 'itemsDirection', target: 'direction' }],
        enhancer: direction => castToInheritIfNeeded(direction, 'ltr'),
      },
    ]),
  ],
);

export const stateRefs = withStateRefsValues([
  'observeChildListChange',
  'observeChildListChangeMaster',
  'reducedMotion',
  'experiments',
]);

export default createComponentMapperModel({ props, css, stateRefs });
